import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { ReactComponent as SwitchDeviceIcon } from '../../assets/images/switch-device.svg';
import { useCall } from '../../customer-call-sdk';
import { useToast } from '../../context/ToastContext';
import { useNotification } from '../../context/NotificationContext';
import Messages from '../../config/messages.constants';

const OfferIcon = styled(SwitchDeviceIcon)`
    width: 20px;
    height: 20px;
`;

const AcceptButton = styled.button`
    min-width: 62px;
    padding: 5px 8px;
    border-radius: 500px;
    background: #ffffff;
    color: #015ebb;
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 18px;

    &:focus:not(:focus-visible) {
        outline: none;
    }

    &:hover {
        background: #f2f2f2;
    }

    &:active {
        background: #e0e0e0;
        color: #013f7d;
    }

    &:disabled {
        cursor: default;
    }
`;

const AcceptAction: React.FC<{ onAccept: () => void }> = ({ onAccept }) => {
    const [accepting, setAccepting] = useState(false);

    return (
        <AcceptButton
            type="button"
            aria-label="Accept Switch Request"
            disabled={accepting}
            onClick={() => {
                setAccepting(true);
                onAccept();
            }}
        >
            {accepting ? <CircularProgress size={16} color="inherit" /> : 'Accept'}
        </AcceptButton>
    );
};

const SwitchDeviceOffer: React.FC = () => {
    const { isSwitchDeviceOffer, switchOfferTitle, switchOfferBody, answer } = useCall();
    const toast = useToast();
    const { showNotification, closeNotifications } = useNotification();
    const isAcceptingRef = useRef(false);

    const handleAccept = useCallback(() => {
        if (isAcceptingRef.current) {
            return;
        }
        isAcceptingRef.current = true;
        answer();
    }, [answer]);

    useEffect(() => {
        if (!isSwitchDeviceOffer) {
            return;
        }
        isAcceptingRef.current = false;

        // The server words it for the target device; the local strings cover an older API.
        showNotification(
            switchOfferTitle || Messages.SWITCH_DEVICE_OFFER_NOTIFICATION_TITLE,
            switchOfferBody || Messages.SWITCH_DEVICE_OFFER_NOTIFICATION_BODY,
            true,
        );

        const id = toast.info(Messages.SWITCH_DEVICE_OFFER_MESSAGE, {
            banner: 2,
            icon: <OfferIcon />,
            action: () => <AcceptAction onAccept={handleAccept} />,
        });

        return () => {
            toast.close(id);
            closeNotifications();
        };
    }, [
        isSwitchDeviceOffer,
        switchOfferTitle,
        switchOfferBody,
        handleAccept,
        toast,
        showNotification,
        closeNotifications,
    ]);

    return null;
};

export default SwitchDeviceOffer;
